import { useState } from "react";
import { NewFeatureBadge } from "../new-feature-badge";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useGetPdfTemplate, getGetPdfTemplateQueryKey, useApplyQuotePdfTemplate } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Palette } from "lucide-react";
import { apiErrorMessage } from "./pdf-template-utils";

export function PdfTemplateDesignPrompt({ currentVersion }: { currentVersion: string | null }) {
  const query = useGetPdfTemplate({ query: { queryKey: getGetPdfTemplateQueryKey(), staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true } });
  if (currentVersion || !query.isSuccess || query.isFetching || query.data?.design !== null || query.data?.version !== null) return null;
  return (
    <Button size="sm" variant="outline" asChild>
      <Link href="/quote-document-design" data-testid="button-design-your-quote">
        <Palette className="mr-1 h-3.5 w-3.5" />
        <span className="text-xs">Design Your Quote</span>
        <NewFeatureBadge testId="badge-design-your-quote-new" />
      </Link>
    </Button>
  );
}

export function PdfTemplateQuoteControls({ id, currentVersion }: { id: string; currentVersion: string | null }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data } = useGetPdfTemplate({ query: { queryKey: getGetPdfTemplateQueryKey(), staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true } });
  const apply = useApplyQuotePdfTemplate();
  const [confirm, setConfirm] = useState<"apply" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const savedVersion = data?.version ?? null;
  const hasSaved = !!data?.design && !!savedVersion;
  const upToDate = !!currentVersion && currentVersion === savedVersion;

  const run = (useDefault: boolean) => {
    setError(null);
    apply.mutate({ id, data: { useDefault } }, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["/api/quotes", id] });
        qc.invalidateQueries({ queryKey: ["/api/quotes"] });
        toast({ title: useDefault ? "Saved design applied to this quote" : "Original style restored" });
      },
      onError: (e) => setError(apiErrorMessage(e, "The design could not be changed on this quote.")),
    });
    setConfirm(null);
  };

  // Before a design exists, the download toolbar offers the direct design link.
  if (!hasSaved && !currentVersion) return null;

  return (
    <div className="rounded-lg border bg-card p-3" data-testid="pdf-template-quote-controls">
      <div className="flex items-center gap-3 overflow-x-auto" data-testid="quote-design-header">
        <div className="flex shrink-0 items-center gap-2.5">
          <Palette className="h-4 w-4 shrink-0 text-primary" />
          <p className="flex items-center gap-2 whitespace-nowrap text-sm font-medium">
            Quote Design
            <NewFeatureBadge testId="badge-document-design-new" />
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link href="/quote-document-design" data-testid="button-open-quote-design">Design Your Quote</Link>
          </Button>
          <Button size="sm" variant="outline" disabled={!hasSaved || upToDate || apply.isPending} onClick={() => setConfirm("apply")} data-testid="button-apply-quote-template">
            {apply.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {currentVersion ? "Update to saved design" : "Apply saved design"}
          </Button>
          {currentVersion && (
            <Button size="sm" variant="ghost" disabled={apply.isPending} onClick={() => setConfirm("remove")} data-testid="button-remove-quote-template">Use original style</Button>
          )}
        </div>
      </div>
      <p className="mt-2 pl-[26px] text-xs text-muted-foreground" data-testid="quote-design-description">
        Create your quote design before downloading or sending it to your customer.
      </p>
      {error && <p className="mt-1 pl-[26px] text-xs text-destructive" data-testid="text-quote-template-error">{error}</p>}
      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "apply" ? "Apply your saved design to this quote?" : "Return this quote to the original style?"}</AlertDialogTitle>
            <AlertDialogDescription>
              If you have already sent this quote, the customer's online copy and any new PDFs will change to match. PDFs already downloaded or emailed stay as they were.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-quote-template">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(confirm === "apply")} data-testid="button-confirm-quote-template">
              {confirm === "apply" ? "Apply design" : "Use original style"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
